import { useId, useState, type JSX, type MouseEvent } from 'react';
import * as AlertDialog from '@radix-ui/react-alert-dialog';
import { TrashIcon } from '@radix-ui/react-icons';
import clsx from 'clsx';
import { toast } from 'sonner';
import { Popover, PopoverAnchor, PopoverContent, PopoverPortal } from '@radix-ui/react-popover';

import { PillButton } from '@/components/entity/PillButton';
import { useDashboardUser } from '@/routes/dashboard/DashboardRouter';
import { usePropertyDeleteMutation } from '@/hooks/useDashboardProperties';
import { isServiceError } from '@/lib/services/errors';
import type { PropertySummary } from '@/lib/types/property';
import { formatPropertyAddress } from '@/lib/utils/propertyFormatting';
import { propertyActionIconClassName } from '@/components/dashboard/properties/PropertyActionIconButton';

type ButtonVariant = 'primary' | 'secondary';
type ButtonSize = 'sm' | 'md';

interface PropertyDeleteButtonProps {
  property: PropertySummary;
  variant?: ButtonVariant;
  size?: ButtonSize;
  fullWidth?: boolean;
  className?: string;
  mode?: 'text' | 'icon';
  onDeleted?: () => void;
}

export function PropertyDeleteButton({
  property,
  variant = 'secondary',
  size = 'md',
  fullWidth = false,
  className,
  mode = 'text',
  onDeleted,
}: PropertyDeleteButtonProps): JSX.Element | null {
  const user = useDashboardUser();
  const mutation = usePropertyDeleteMutation({ user });
  const [open, setOpen] = useState(false);
  const [popoverOpen, setPopoverOpen] = useState(false);
  const popoverId = `property-delete-${useId()}`;

  const isDeleting = mutation.isPending;

  const triggerClassName = clsx(fullWidth && 'w-full', className);
  const iconTrigger = (
    <Popover open={popoverOpen} onOpenChange={setPopoverOpen}>
      <PopoverAnchor asChild>
        <AlertDialog.Trigger asChild>
          <button
            type="button"
            aria-label="Delete property"
            aria-haspopup="dialog"
            aria-controls={popoverId}
            aria-expanded={popoverOpen}
            className={clsx(
              propertyActionIconClassName,
              'border-red-200 text-red-700 hover:border-red-400 hover:bg-red-50',
              triggerClassName,
            )}
            onMouseEnter={() => {
              setPopoverOpen(true);
            }}
            onMouseLeave={() => {
              setPopoverOpen(false);
            }}
            onFocus={() => {
              setPopoverOpen(true);
            }}
            onBlur={() => {
              setPopoverOpen(false);
            }}
            onClick={(event: MouseEvent<HTMLButtonElement>) => {
              mutation.reset();
              event.currentTarget.blur();
            }}
          >
            <TrashIcon className="h-4 w-4" />
            <span className="sr-only">Delete property</span>
          </button>
        </AlertDialog.Trigger>
      </PopoverAnchor>
      <PopoverPortal>
        <PopoverContent
          id={popoverId}
          side="top"
          align="center"
          sideOffset={8}
          collisionPadding={12}
          aria-label="Delete property help"
          className="rounded-md border border-brand-primary/20 bg-brand-primary px-3 py-1 text-xs font-medium text-white shadow-lg"
        >
          Delete property
        </PopoverContent>
      </PopoverPortal>
    </Popover>
  );
  const textTrigger = (
    <AlertDialog.Trigger asChild>
      <PillButton
        type="button"
        variant={variant}
        size={size}
        className={clsx(
          'border-red-200 text-red-700 hover:border-red-400 hover:bg-red-50',
          triggerClassName,
        )}
        onClick={() => {
          mutation.reset();
        }}
      >
        Delete
      </PillButton>
    </AlertDialog.Trigger>
  );

  const handleConfirm = () => {
    mutation.mutate(
      { property },
      {
        onSuccess: () => {
          toast.success(`Deleted ${formatPropertyAddress(property.address)}.`);
          setOpen(false);
          onDeleted?.();
        },
        onError: (reason) => {
          const message = formatPropertyDeleteError(reason);
          toast.error(message);
        },
      },
    );
  };

  return (
    <AlertDialog.Root open={open} onOpenChange={setOpen}>
      {mode === 'icon' ? iconTrigger : textTrigger}
      <AlertDialog.Portal>
        <AlertDialog.Overlay
          className="fixed inset-0 z-40 bg-black/40 backdrop-blur-sm"
          onPointerDown={() => {
            setOpen(false);
          }}
        />
        <AlertDialog.Content className="fixed inset-x-0 bottom-0 z-50 flex max-h-screen flex-col rounded-t-3xl border-t border-surface-subtle bg-white shadow-2xl focus:outline-none md:inset-y-0 md:left-auto md:right-0 md:w-full md:max-w-lg md:rounded-none md:border-l">
          <div className="flex flex-col gap-3 p-5">
            <AlertDialog.Title className="text-lg font-semibold text-text-primary">
              Delete property
            </AlertDialog.Title>
            <AlertDialog.Description className="text-sm text-text-primary/70">
              This removes {formatPropertyAddress(property.address)} from the dashboard. This action
              cannot be undone.
            </AlertDialog.Description>
            <div className="flex flex-col gap-2 text-sm text-text-primary/80">
              <span className="font-semibold">Property code:</span>
              <span className="bg-surface-muted/40 rounded-xl border border-surface-subtle px-3 py-2 text-text-primary">
                {property.propertyCode}
              </span>
            </div>
            <div className="mt-2 flex items-center justify-end gap-3">
              <AlertDialog.Cancel asChild>
                <button
                  type="button"
                  className="rounded-full px-4 py-2 text-sm font-semibold text-text-primary/70 transition hover:bg-surface-subtle"
                  disabled={isDeleting}
                >
                  Cancel
                </button>
              </AlertDialog.Cancel>
              <AlertDialog.Action asChild>
                <PillButton
                  type="button"
                  className="border-red-200 bg-red-600 text-white hover:bg-red-700"
                  disabled={isDeleting}
                  onClick={handleConfirm}
                >
                  {isDeleting ? 'Deleting…' : 'Delete property'}
                </PillButton>
              </AlertDialog.Action>
            </div>
          </div>
        </AlertDialog.Content>
      </AlertDialog.Portal>
    </AlertDialog.Root>
  );
}

export function formatPropertyDeleteError(error: unknown): string {
  const base =
    "Can't delete this property yet. It's still linked to other records. Remove or reassign them first.";

  const detailSource = extractDetail(error);
  const related =
    typeof detailSource === 'string' && detailSource.length > 0
      ? extractRelatedReasons(detailSource)
      : [];

  if (related.length > 0) {
    const friendlyList = formatList(related);
    return `Can't delete this property yet. It's still linked to ${friendlyList}. Remove or reassign them first.`;
  }

  return base;
}

function extractDetail(error: unknown): string | null {
  if (isServiceError(error)) {
    if (typeof error.details === 'string' && error.details.length > 0) {
      return error.details;
    }
    if (typeof error.message === 'string' && error.message.length > 0) {
      return error.message;
    }
  }

  if (error instanceof Error && typeof error.message === 'string') {
    return error.message;
  }

  return null;
}

function extractRelatedReasons(message: string): string[] {
  const normalized = message.toLowerCase();
  const reasons: string[] = [];

  if (normalized.includes('units')) {
    reasons.push('units under this property');
  }
  if (normalized.includes('leads')) {
    reasons.push('applications tied to this property');
  }
  if (normalized.includes('property_floors')) {
    reasons.push('floor plans for this property');
  }

  return Array.from(new Set(reasons));
}

function formatList(items: string[]): string {
  if (items.length === 1) {
    return items[0];
  }
  if (items.length === 2) {
    return `${items[0]} and ${items[1]}`;
  }
  const allButLast = items.slice(0, -1).join(', ');
  return `${allButLast}, and ${items[items.length - 1]}`;
}
